import api from './api'

/**
 * Player Service
 * Handles all player-related API calls
 */
class PlayerService {
  constructor() {
    // Service initialized
  }
  /**
   * Get all players for a club with filters
   * GET v1/{club}/players
   */
  async getPlayers(clubUuid: string, params?: any): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/players`, { params })
    return response.data
  }

  /**
   * Get single player
   * GET v1/{club}/players/{player}
   */
  async getPlayer(clubUuid: string, playerUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/players/${playerUuid}`)
    return response.data
  }

  /**
   * Create new player
   * POST v1/{club}/players
   */
  async createPlayer(clubUuid: string, formData: FormData): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/players`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  /**
   * Update player
   * PUT v1/{club}/players/{player}
   */
  async updatePlayer(clubUuid: string, playerUuid: string, formData: FormData): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/players/${playerUuid}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        _method: 'PUT'
      }
    })
    return response.data
  }

  /**
   * Delete player
   * DELETE v1/{club}/players/{player}
   */
  async deletePlayer(clubUuid: string, playerUuid: string): Promise<any> {
    const response = await api.delete(`v1/${clubUuid}/players/${playerUuid}`)
    return response.data
  }

  /**
   * Get monthly attendance stats for player
   * GET v1/{club}/players/{player}/attendance-stats
   */
  async getAttendanceStats(clubUuid: string, playerUuid: string, date: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/players/${playerUuid}/attendance-stats`, {
      params: { date }
    })
    return response.data
  }

  /**
   * Get recent attendance for player with pagination
   * GET v1/{club}/players/{player}/recent-attendance
   */
  async getRecentAttendance(clubUuid: string, playerUuid: string, perPage: number = 25, page: number = 1): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/players/${playerUuid}/recent-attendance`, {
      params: {
        per_page: perPage,
        page: page
      }
    })
    return response.data
  }

  /**
   * Get player payments
   * GET v1/{club}/players/{player}/payments
   */
  async getPlayerPayments(clubUuid: string, playerUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/players/${playerUuid}/payments`)
    return response.data
  }

  /**
   * Bulk add category to players
   * POST v1/{club}/players/bulk/add-category
   */
  async bulkAddCategory(clubUuid: string, payload: any): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/players/bulk/add-category`, payload)
    return response.data
  }

  /**
   * Bulk remove category from players
   * POST v1/{club}/players/bulk/remove-category
   */
  async bulkRemoveCategory(clubUuid: string, payload: any): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/players/bulk/remove-category`, payload)
    return response.data
  }

  /**
   * Bulk send message to players
   * POST v1/{club}/players/bulk/send-message
   */
  async bulkSendMessage(clubUuid: string, payload: any): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/players/bulk/send-message`, payload)
    return response.data
  }

  /**
   * Bulk add payment to players
   * POST v1/{club}/players/bulk/add-payment
   */
  async bulkAddPayment(clubUuid: string, payload: any): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/players/bulk/add-payment`, payload)
    return response.data
  }

  /**
   * Bulk export players to Excel
   * POST v1/{club}/players/bulk/export
   */
  async bulkExport(clubUuid: string, payload: any): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/players/bulk/export`, payload)
    return response.data
  }

  /**
   * Resend invitation email to player
   * POST v1/{club}/players/{player}/resend-invitation
   */
  async resendInvitation(clubUuid: string, playerUuid: string): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/players/${playerUuid}/resend-invitation`)
    return response.data
  }
}

export default new PlayerService()
